import React, { useEffect, useMemo, useRef, useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import { useTheme } from "@mui/material/styles";
import Accordion from "@components/Accordion/Accordion";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import ConfirmUnclaimDialog from "@components/ConfirmUnclaimDialog/ConfirmUnclaimDialog";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import FilePreview from "@components/FilePreview/FilePreview";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import SideDrawer from "@components/SideDrawer/SideDrawer";
import type { DrawerType, SideDrawerAttachmentRef } from "@components/SideDrawer/SideDrawer.types";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { getBorderBoxStyle } from "@features/port-security/components/security-clearance-request-preview/SecurityClearanceRequestPreview.styles";
import UserDetailsCard from "@features/port-security/components/security-clearance-request-preview/UserDetailsCard/UserDetailsCard";
import UserProfileCard from "@features/port-security/components/security-clearance-request-preview/UserProfileCard/UserProfileCard";
import type {
  Company,
  SecurityClearanceError,
  ValidityPeriodType,
  VisaType,
} from "@features/port-security/components/SecurityClearence.types.ts";
import {
  extractVerificationDetails,
  formatDateFull,
  getLatestSecurityClearanceDecisionEvent,
  getLatestTaskWithReason,
  getSecurityClearanceBadgeType,
  getSortedTaskHistory,
  getValidityPeriodsOptions,
  resolveSecurityClearanceDisplayStatus,
  SECURITY_CLEARANCE_STATUS,
  securityClearanceDecisionHasViewableDetails,
  toDateOnlyString,
  validityPeriodIntToString,
} from "@features/port-security/components/utils.ts";
import { useCompleteSecurityClearance } from "@features/port-security/hooks/useCompleteSecurityClearance";
import { useDownloadSecurityClearanceAttachments } from "@features/port-security/hooks/useDownloadSecurityClearanceAttachments";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useRestrictedNationalities } from "@hooks/useRestrictedNationalities";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { SecurityClearanceTaskHistoryEntry } from "@services/security-clearance/types";
import {
  cancelSecurityClearance,
  claimSecurityClearance,
  getMySecurityClearance,
  getSecurityClearanceById,
  getSecurityClearancePrintDocument,
  permanentlyRejectSecurityClearanceById,
  suspendSecurityClearanceById,
  unclaimSecurityClearance,
  unsuspendSecurityClearanceById,
} from "@services/securityClearanceApi";
import { SasTokenPrefix, SasTokenPrivilege, type UserResponse } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { Country } from "@services/vessel/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { formatDateToDDMMYYYY } from "@utils/date-time";
import { stringToTitleCase } from "@utils/formatters/strings";
import { paths } from "@utils/navigation";
import { printRequestedTemplate } from "@utils/printRequestedTemplate";
import { checkPermission, isIndividualApplicantUser } from "@utils/user-permissions";
import { addDays, format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const LANG_BASE = "security-clearance";
const TEST_ID_BASE = "security-clearance-request-preview";
const service = "bff-gatepass";
const DECISION_ATTACHMENT_UPLOAD_KEY = "sideDrawerApprovalFile";
const SECURITY_CLEARANCE_PREVIEW_REFRESH_RETRY_MS = 1500;

const formatDateOnly = (value: string | null | undefined) => {
  const dateOnly = toDateOnlyString(value);
  if (!dateOnly) return "-";
  const [year, month, day] = dateOnly.split("-");
  return `${day}/${month}/${year}`;
};

type OptimisticStatusSnapshot = {
  apiStatus: string;
  taskStatus: string | null;
  tasksHistoryLength: number;
  applicationDate: string | null;
};

const normalizeSecurityClearanceApiStatus = (status: string | null | undefined) => status ?? "NULL";

type SecurityClearancePreviewData = {
  status?: string | null;
  task?: { status?: string } | null;
  tasksHistory?: SecurityClearanceTaskHistoryEntry[] | null;
  applicationDate?: string | null;
};

const createOptimisticStatusSnapshot = (
  refetchData: SecurityClearancePreviewData | undefined,
): OptimisticStatusSnapshot => ({
  apiStatus: normalizeSecurityClearanceApiStatus(refetchData?.status),
  taskStatus: refetchData?.task?.status ?? null,
  tasksHistoryLength: refetchData?.tasksHistory?.length ?? 0,
  applicationDate: refetchData?.applicationDate ?? null,
});

const hasSecurityClearanceDataMovedOn = (
  currentData: SecurityClearancePreviewData,
  snapshot: OptimisticStatusSnapshot | null,
  baselineApiStatus: string | null,
) => {
  if (baselineApiStatus == null || !snapshot) return false;

  const apiStatus = normalizeSecurityClearanceApiStatus(currentData.status);

  return (
    apiStatus !== baselineApiStatus ||
    (currentData.task?.status ?? null) !== snapshot.taskStatus ||
    (currentData.tasksHistory?.length ?? 0) !== snapshot.tasksHistoryLength ||
    (currentData.applicationDate ?? null) !== snapshot.applicationDate
  );
};

let syncAuthStateRequestId = 0;
const syncAuthState = async (
  scId: string | undefined | null,
  scStatus: string | undefined | null,
  user: UserResponse | null,
) => {
  const currentRequestId = ++syncAuthStateRequestId;

  let myScId: string | null, myScStatus: string | null;

  try {
    myScId = (
      (await getMySecurityClearance({ service })) as unknown as {
        securityClearanceId: string | null;
      }
    ).securityClearanceId;
    if (!myScId) {
      throw Error;
    }
    myScStatus = (
      await getSecurityClearanceById({
        id: myScId,
        service: "bff-gatepass",
      })
    ).status;
  } catch {
    myScId = scId || null;
    myScStatus = scStatus || null;
  }

  if (currentRequestId === syncAuthStateRequestId) {
    const { setSecurityClearanceRequestID, setSecurityClearanceRequestStatus, setUser } =
      useAuthStore.getState();

    const idToSet = myScId || scId || null;
    const statusToSet = myScStatus ? String(myScStatus).toUpperCase() : null;
    setSecurityClearanceRequestID(idToSet);
    // Always update request status if backend returned it
    if (typeof scStatus === "string") {
      setSecurityClearanceRequestStatus(statusToSet);
      if (user) {
        setUser(user, idToSet, statusToSet, {
          updateBootstrap: user.roles.some((role) =>
            ["security-applicant", "uae-pass-user"].includes(role),
          ),
        });
      }
    }
  }
};

const SecurityClearanceRequestPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const { options: licensedCompanies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isLicensedCompany=true`,
    "bff-gatepass",
  );
  const { options: allCompanies } = useFormattedReference<Company>(
    REFERENCE_DATA_TYPES.COMPANIES,
    "bff-gatepass",
  );
  const navigate = useNavigate();
  const { notify } = useNotification();
  const {
    user,
    userActionPermissions,
    setSecurityClearanceRenewID,
    userCustomStatuses,
    setSecurityClearanceRequestStatus,
  } = useAuthStore();
  const isIndividualRole = isIndividualApplicantUser(user?.roles);
  const canDirectApproveSecurityClearance = checkPermission(
    userActionPermissions,
    "canDirectlyApproveSecurityClearance",
  );
  const componentRef = useRef<HTMLDivElement>(null);
  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
    `${TEST_ID_BASE}-self-introduction-1`,
  );
  const [requestingSponsorCompany, setRequestingSponsorCompany] = useState<Company | undefined>(
    undefined,
  );
  const [submittedByCompany, setSubmittedByCompany] = useState<Company | undefined>(undefined);
  // Temporary optimistic overlay until backend status catches up after local actions
  const [fakeStatus, setFakeStatus] = useState<string>("");
  const fakeStatusBaselineRef = useRef<string | null>(null);
  const fakeStatusSnapshotRef = useRef<OptimisticStatusSnapshot | null>(null);

  const applyOptimisticStatus = (
    optimisticStatus: string | null | undefined,
    refetchData: SecurityClearancePreviewData | undefined,
  ) => {
    fakeStatusBaselineRef.current = normalizeSecurityClearanceApiStatus(refetchData?.status);
    fakeStatusSnapshotRef.current = createOptimisticStatusSnapshot(refetchData);
    setFakeStatus(optimisticStatus ?? "");
  };

  const clearOptimisticStatus = () => {
    fakeStatusBaselineRef.current = null;
    fakeStatusSnapshotRef.current = null;
    setFakeStatus("");
  };

  const [suspentionReason, setSuspentionReason] = useState<string>("");
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);
  const [unsuspendDialogOpen, setUnsuspendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const suspendSubmitLockedRef = useRef(false);
  const [isUnsuspendActionLocked, setIsUnsuspendActionLocked] = useState(false);
  const [isUnsuspendSubmitLocked, setIsUnsuspendSubmitLocked] = useState(false);
  const unsuspendActionLockedRef = useRef(false);
  const unsuspendRequestStartedRef = useRef(false);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [lastCompletionType, setLastCompletionType] = useState<"APPROVED" | "RESUBMIT" | null>(
    null,
  );
  const [isCompletionSubmitLocked, setIsCompletionSubmitLocked] = useState(false);
  const completionSubmitLockedRef = useRef(false);
  const pendingCompletionTypeRef = useRef<"APPROVED" | "RESUBMIT" | null>(null);
  const [uploadedDecisionAttachments, setUploadedDecisionAttachments] = useState<
    { url: string; originalFileName: string }[]
  >([]);
  const isDecisionAttachmentUploadInFlight = useUploadStore((state) =>
    state.isUploadInFlight(DECISION_ATTACHMENT_UPLOAD_KEY),
  );

  useEffect(() => {
    if (!drawerType) return;
    setUploadedDecisionAttachments([]);
  }, [drawerType]);

  const canChangeValidityPeriod = checkPermission(
    userActionPermissions,
    "canChangeSecurityClearanceValidityPeriod",
  );

  const isSecuritySupervisor = Array.isArray(user?.roles)
    ? user!.roles.includes("security-supervisor")
    : false;
  const isSecurityOperator = Array.isArray(user?.roles)
    ? user.roles.includes("security-operator")
    : false;
  const isSecurityDeptActor = isSecurityOperator || isSecuritySupervisor;

  const { options: validityPeriods } = useFormattedReference<ValidityPeriodType>(
    REFERENCE_DATA_TYPES.SECURITY_CLEARANCE_VALIDITY_PERIODS,
    "bff-gatepass",
  );
  const { options: restrictedNationalities } = useRestrictedNationalities();

  const { options: visaTypes } = useFormattedReference<VisaType>(
    REFERENCE_DATA_TYPES.VISA_TYPES,
    "bff-gatepass",
  );

  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-gatepass",
  );

  const getVisaTypeLabel = (code?: string) => {
    if (!code) return "-";
    const match = visaTypes.find((v) => v.typeCode === code);
    return match?.typeName ?? code;
  };

  const getCountryLabel = (value?: string | null) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) return "-";

    const match = countries.find(
      (country) => country.countryCode?.toUpperCase() === normalized.toUpperCase(),
    );

    return stringToTitleCase(match?.countryName ?? normalized);
  };

  const handlePrint = async () => {
    await printRequestedTemplate({
      id,
      service,
      requestTemplateDocument: getSecurityClearancePrintDocument,
      notify,
      t,
    });
  };

  const { downloadAttachment, handlePreview } = useDownloadSecurityClearanceAttachments({
    notify,
    t,
  });
  const downloadedFilePreview = (documentUrl: string) => {
    if (!documentUrl) {
      console.error("Document URL is required");
      return;
    }

    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };
  const handleAttachmentDownload = async (url: string) => {
    await downloadAttachment(url);
  };

  const suspendTaskMutation = useMutation({
    mutationFn: (reason?: string) =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "suspend_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          suspendSecurityClearanceById({
            service,
            securityClearanceId: id!,
            params: { suspensionReason: reason ?? null },
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: (_res, reason) => {
      suspendSubmitLockedRef.current = false;
      setSuspentionReason(reason ?? ""); // will be deleted after BE implementation
      setDrawerType(null);
      notify({ title: t(`${LANG_BASE}.security-clearance-request-suspended`), type: "success" });
      void refetchPreviewAfterMutation().then((result) => {
        applyOptimisticStatus(userCustomStatuses?.statusUpponSuspending, result.data);
      });
    },
    onError: (error: Error) => {
      setDrawerType(null);
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      suspendSubmitLockedRef.current = false;
    },
  });

  const unsuspendTaskMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "unsuspend_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          unsuspendSecurityClearanceById({
            service,
            securityClearanceId: id!,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: () => {
      applyOptimisticStatus(SECURITY_CLEARANCE_STATUS.APPROVED, data);
      setDrawerType(null);
      notify({ title: t(`${LANG_BASE}.security-clearance-request-unsuspended`), type: "success" });
      void refetchPreviewAfterMutation().then((result) => {
        applyOptimisticStatus(SECURITY_CLEARANCE_STATUS.APPROVED, result.data);
      });
    },
    onError: (error: Error) => {
      setDrawerType(null);
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      unsuspendRequestStartedRef.current = false;
      unsuspendActionLockedRef.current = false;
      setIsUnsuspendActionLocked(false);
      setIsUnsuspendSubmitLocked(false);
    },
  });

  const claimMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "claim_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          claimSecurityClearance({
            service,
            securityClearanceId: id!,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      await refetch();
      clearOptimisticStatus();
      notify({
        title: t(`${LANG_BASE}.security-clearance-request-claimed`),
        type: "success",
      });
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const unclaimMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "unclaim_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          unclaimSecurityClearance({
            service,
            securityClearanceId: id!,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      await refetch();
      clearOptimisticStatus();
      notify({
        title: t(`${LANG_BASE}.security-clearance-request-unclaimed`),
        type: "success",
      });
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      setUnclaimDialogOpen(false);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "cancel_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          cancelSecurityClearance({
            service,
            securityClearanceId: id!,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      const result = await refetch();
      applyOptimisticStatus(userCustomStatuses?.statusUpponCancelling, result.data);
      notify({
        title: t(`${LANG_BASE}.security-clearance-request-cancelled`),
        type: "success",
      });
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const permanentlyRejectMutation = useMutation({
    mutationFn: (reason?: string) =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "permanently_reject_security_clearance",
          entityType: "security_clearance",
        },
        (transactionId) =>
          permanentlyRejectSecurityClearanceById({
            service,
            securityClearanceId: id!,
            params: { rejectionReason: reason ?? "" },
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (_res, reason) => {
      const result = await refetch();
      applyOptimisticStatus(userCustomStatuses?.statusUpponRejecting, result.data);
      setRejectionReason(reason ?? ""); // will be deleted after BE implementation
      setDrawerType(null);
      notify({
        title: t(`${LANG_BASE}.security-clearance-request-rejected`),
        type: "success",
      });
    },
    onError: async (error: SecurityClearanceError) => {
      await refetch();
      setDrawerType(null);
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const uploadDocuments = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      return runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: "upload_security_clearance_document",
          entityType: "security_clearance_document",
        },
        async (transactionId) => {
          const { blobUrl, sasToken } = await getSasToken(
            {
              prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            },
            ...(transactionId ? [transactionId] : []),
          );

          return uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: "SECURITY_CLEARANCE_ATTACHMENT",
          });
        },
      );
    },
    onMutate: () => {
      useUploadStore.getState().beginUploadInFlight(DECISION_ATTACHMENT_UPLOAD_KEY);
    },
    onSuccess: (dataArray, variables) => {
      if (
        Array.isArray(dataArray) &&
        dataArray.length &&
        variables?.file &&
        variables.file instanceof File
      ) {
        const originalFileName = variables.file.name;
        setUploadedDecisionAttachments((prev) => {
          const merged = [...prev];
          dataArray.forEach((d) => {
            const u = d.documentUrl;
            if (u && typeof u === "string" && !merged.some((m) => m.url === u)) {
              merged.push({ url: u, originalFileName });
            }
          });
          return merged;
        });
      }
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      useUploadStore.getState().endUploadInFlight(DECISION_ATTACHMENT_UPLOAD_KEY);
    },
  });
  const isDecisionAttachmentUploading = Boolean(
    uploadDocuments.isPending || isDecisionAttachmentUploadInFlight,
  );

  const handleSubmit = async (data?: { description?: string; validityPeriod?: number }) => {
    if (isDecisionAttachmentUploading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const reason = data?.description?.trim() ?? "";
    const validityPeriod = data?.validityPeriod;

    if (drawerType === "suspend") {
      if (suspendSubmitLockedRef.current) return;

      suspendSubmitLockedRef.current = true;
      suspendTaskMutation.mutate(reason);
      return;
    }

    if (drawerType === "permanentReject") {
      permanentlyRejectMutation.mutate(reason);
      return;
    }

    if (drawerType === "resubmit" && !reason) return;

    if (completionSubmitLockedRef.current) return;
    completionSubmitLockedRef.current = true;
    setIsCompletionSubmitLocked(true);

    const statusToSubmit = drawerType === "approval" ? "APPROVED" : "RESUBMIT";
    pendingCompletionTypeRef.current = statusToSubmit;
    setLastCompletionType(statusToSubmit);

    const payload: {
      status: "APPROVED" | "RESUBMIT";
      reason: string;
      validityPeriod?: number;
      attachments?: Array<{ url: string; type: string; originalFileName: string }>;
    } = {
      status: statusToSubmit,
      reason,
    };

    const validityPeriodNum = validityPeriod ?? undefined;
    if (
      shouldShowValidityPeriod &&
      validityPeriodNum !== undefined &&
      !Number.isNaN(validityPeriodNum)
    ) {
      payload.validityPeriod = validityPeriodNum;
    }
    if (uploadedDecisionAttachments.length) {
      payload.attachments = uploadedDecisionAttachments.map((a) => ({
        url: a.url,
        originalFileName: a.originalFileName,
        type: "SECURITY_CLEARANCE_ATTACHMENT",
      }));
    }

    await completeSecurityClearance({
      id: id!,
      service,
      payload,
    });
  };

  const handleSecurityOperatorApprovalSubmit = async () => {
    if (completionSubmitLockedRef.current) return;
    completionSubmitLockedRef.current = true;
    setIsCompletionSubmitLocked(true);
    pendingCompletionTypeRef.current = "APPROVED";
    setLastCompletionType("APPROVED");
    await completeSecurityClearance({
      id: id!,
      service,
      payload: {
        status: "APPROVED",
        reason: "",
      },
    });
  };

  const handleApproveClick = () => {
    if (shouldDirectVerifySecurityClearance) {
      handleSecurityOperatorApprovalSubmit();
    } else {
      setDrawerType("approval");
    }
  };

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["security-clearance-preview", id],
    queryFn: () => getSecurityClearanceById({ id: id!, service: service }),
    enabled: !!id,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  async function refetchPreviewAfterMutation() {
    const result = await refetch();
    window.setTimeout(() => {
      void refetch();
    }, SECURITY_CLEARANCE_PREVIEW_REFRESH_RETRY_MS);
    return result;
  }

  const handleSuccess = async () => {
    const completionType = pendingCompletionTypeRef.current ?? lastCompletionType;
    const isApproval = completionType === "APPROVED";
    const approvingFakeStatus = (() => {
      switch (requestStatus) {
        case SECURITY_CLEARANCE_STATUS.PENDING_SPONSOR_COMPANY_APPROVAL:
          return SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL;
        case SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL:
          return SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL;
        case SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL:
          return SECURITY_CLEARANCE_STATUS.APPROVED;
        default:
          return userCustomStatuses?.statusUpponApproving;
      }
    })();
    const fakeStatusToPass = isApproval
      ? approvingFakeStatus
      : userCustomStatuses?.statusUpponResubmiting;

    const result = await refetch();
    applyOptimisticStatus(fakeStatusToPass, result.data);
    pendingCompletionTypeRef.current = null;
    completionSubmitLockedRef.current = false;
    setIsCompletionSubmitLocked(false);

    let notificationTitleKey: string;

    // select the correct message based on the status and the peformed action
    if (isApproval) {
      if (data?.status === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL) {
        notificationTitleKey = "security-clearance-request-verified";
      } else if (data?.status === SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL) {
        notificationTitleKey = "security-clearance-request-cid-approved";
      } else {
        notificationTitleKey = "security-clearance-request-approved";
      }
    } else {
      notificationTitleKey = "security-clearance-request-rejected";
    }

    setDrawerType(null);
    const shouldShowRefreshHint = isApproval && isSecuritySupervisor && isSecurityVerificationStage;

    notify({
      title: t(`${LANG_BASE}.${notificationTitleKey}`),
      description: shouldShowRefreshHint ? t(`${LANG_BASE}.notifications.refresh-page`) : undefined,
      type: "success",
    });
  };

  const handleError = async (error: SecurityClearanceError) => {
    await refetch();
    completionSubmitLockedRef.current = false;
    setIsCompletionSubmitLocked(false);
    setDrawerType(null);
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeSecurityClearance } = useCompleteSecurityClearance({
    handleSuccess,
    handleError,
  });

  useEffect(() => {
    if (!data || !fakeStatus) return;

    if (
      hasSecurityClearanceDataMovedOn(
        data,
        fakeStatusSnapshotRef.current,
        fakeStatusBaselineRef.current,
      )
    ) {
      clearOptimisticStatus();
    }
  }, [data, dataUpdatedAt, fakeStatus]);

  const requestStatus = useMemo(() => {
    if (!data) return "";
    return resolveSecurityClearanceDisplayStatus({
      apiStatus: normalizeSecurityClearanceApiStatus(data.status),
      fakeStatus,
      taskStatus: data.task?.status ?? null,
      tasksHistory: (data as SecurityClearancePreviewData).tasksHistory ?? null,
    });
  }, [data, dataUpdatedAt, fakeStatus]);

  const isClaimed = data?.task?.status === "CLAIMED";
  const isOwner = data?.task?.claimOwnerName === user?.preferredUsername;

  const isSecurityVerificationStage = requestStatus === "PENDING_SECURITY_DEPT_APPROVAL";
  const isSecurityApprovalStage = requestStatus === "PENDING_SECURITY_SUPERVISOR_APPROVAL";
  const isSponsorCompanyStage = requestStatus === "PENDING_SPONSOR_COMPANY_APPROVAL";
  const shouldShowValidityPeriod = drawerType === "approval" && isSecurityApprovalStage;
  const shouldDirectVerifySecurityClearance =
    isSecurityVerificationStage && (canDirectApproveSecurityClearance || isSecuritySupervisor);

  // Use the backend-linked SC for permissions, falling back to the viewed SC if the lookup fails.
  useEffect(() => {
    if (!isIndividualRole) {
      return;
    }
    void syncAuthState(id, data?.status, user);
  }, [id, data?.status, isIndividualRole, user]);

  const handleRenew = () => {
    const nextStatus = data?.status ?? null;
    const renewalId = id!;
    setSecurityClearanceRequestStatus(nextStatus);
    setSecurityClearanceRenewID(renewalId);
    navigate({
      pathname: `/${paths.portSecurity.index}/${paths.portSecurity.requestSecurityClearance}/${paths.portSecurity.clearanceForm}`,
      search: `?renewalId=${encodeURIComponent(renewalId)}&existingSecurityClearanceId=${encodeURIComponent(renewalId)}&requestStatus=${encodeURIComponent(String(nextStatus ?? ""))}`,
    });
  };

  useEffect(() => {
    const incoming = data?.suspension?.reason ?? "";
    if (incoming && incoming !== suspentionReason) {
      setSuspentionReason(incoming);
    }
    if (!data?.suspension && suspentionReason) {
      setSuspentionReason("");
    }
  }, [data?.suspension?.reason, data?.suspension, suspentionReason]);

  useEffect(() => {
    const incoming = data?.permanentRejection?.reason ?? "";
    if (incoming && incoming !== rejectionReason) {
      setRejectionReason(incoming);
    }
    if (!data?.permanentRejection && rejectionReason) {
      setRejectionReason("");
    }
  }, [data?.permanentRejection?.reason, data?.permanentRejection, rejectionReason]);

  useEffect(() => {
    if (!data || !licensedCompanies.length || !allCompanies.length) return;

    const nextRequestingSponsorCompany = licensedCompanies.find(
      (company) => company.companyCode === data.requestingSponsorEnglish,
    );

    setRequestingSponsorCompany((previousCompany) =>
      previousCompany?.companyCode === nextRequestingSponsorCompany?.companyCode
        ? previousCompany
        : nextRequestingSponsorCompany,
    );

    const nextSubmittedByCompany = allCompanies.find(
      (company) => company.companyCode === data.submittedBy,
    );

    setSubmittedByCompany((previousCompany) =>
      previousCompany?.companyCode === nextSubmittedByCompany?.companyCode
        ? previousCompany
        : nextSubmittedByCompany,
    );
  }, [data?.requestingSponsorEnglish, data?.submittedBy, licensedCompanies, allCompanies]);

  if (isLoading) return <div className="p-8">Loading...</div>;
  if (isError || !data) {
    return (
      <div>
        <ErrorHandler error={error} testId={`${TEST_ID_BASE}-error-handler`} />
      </div>
    );
  }

  const {
    applicant: rawApplicant,
    currentSponsor,
    academicQualifications,
    residenceStatus,
    residency,
    vehicleDetails,
    signature,
    documents,
    sponsorOfEntry,
    entryPurposeEnglish,
    entryPurposeArabic,
    validityPeriod,
    duration,
    uidNumber,
    submittedByPreferredUsername,
    submittedBy,
    militaryService,
    task,
    approvalDate,
  } = data;
  const applicant = rawApplicant ?? ({} as typeof rawApplicant);

  const permanentRejectionFromApi = data.permanentRejection;
  const suspensionFromApi = data.suspension;
  const permanentRejectionReasonForDetailsBtn: unknown =
    permanentRejectionFromApi != null && typeof permanentRejectionFromApi === "object"
      ? (permanentRejectionFromApi as { reason?: unknown }).reason
      : undefined;
  const suspensionReasonForDetailsBtn: unknown =
    suspensionFromApi != null && typeof suspensionFromApi === "object"
      ? (suspensionFromApi as { reason?: unknown }).reason
      : undefined;

  const latestDecisionEvent = getLatestSecurityClearanceDecisionEvent({
    tasksHistory: data?.tasksHistory,
    suspension: suspensionFromApi ?? null,
    permanentRejection: permanentRejectionFromApi ?? null,
  });

  const sortedTasksHistory = getSortedTaskHistory(data?.tasksHistory || []);
  const latestTaskFromTimeline =
    latestDecisionEvent?.source === "tasksHistory" && latestDecisionEvent.tasksHistoryEntry
      ? latestDecisionEvent.tasksHistoryEntry
      : undefined;
  const latestTaskLegacy = getLatestTaskWithReason(sortedTasksHistory);
  const latestTask = latestTaskFromTimeline ?? latestTaskLegacy;
  const latestTaskReason = latestTask?.taskDecision?.reason;

  // Task attachments in View details only for the timeline-winning tasksHistory row (not older tasks).
  const timelineDetailsAttachments: SideDrawerAttachmentRef[] | undefined = (() => {
    if (latestDecisionEvent?.source !== "tasksHistory") return undefined;
    const raw = latestDecisionEvent.tasksHistoryEntry?.taskDecision?.attachments;
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    return raw as SideDrawerAttachmentRef[];
  })();

  const hasDetailReasonText = (value: unknown) =>
    typeof value === "string" && value.trim().length > 0;

  // View details only when the latest decision (by timestamp) has notes and/or task attachments,
  // or dedicated permanent rejection / suspension paths (incl. optimistic copy).
  const showViewDetailsButton =
    securityClearanceDecisionHasViewableDetails(latestDecisionEvent) ||
    (requestStatus === "PERMANENTLY_REJECTED" &&
      (permanentRejectionFromApi != null ||
        hasDetailReasonText(permanentRejectionReasonForDetailsBtn) ||
        hasDetailReasonText(rejectionReason))) ||
    (requestStatus === "SUSPENDED" &&
      (suspensionFromApi != null ||
        hasDetailReasonText(suspensionReasonForDetailsBtn) ||
        hasDetailReasonText(suspentionReason)));

  const verificationDetails = extractVerificationDetails(data);
  const validityDays = validityPeriod ?? 0;
  const lastApprovalDate = approvalDate ? new Date(approvalDate) : null;

  const clearanceDurationText = duration?.trim()
    ? formatDateToDDMMYYYY(duration)
    : lastApprovalDate && validityDays > 0
      ? format(addDays(lastApprovalDate, validityDays), "dd/MM/yyyy")
      : "-";

  const attachmentsLabel = t(`${LANG_BASE}.attachments`);

  const canClaimRequest = task?.status === "READY";

  // Check if current user is the creator of this request
  const isCreator = submittedByPreferredUsername === user?.preferredUsername;

  const validityPeriodsOptions = getValidityPeriodsOptions(
    validityPeriods,
    applicant.presentNationality,
    restrictedNationalities,
  );

  // Helper to check if the request belongs to the same company as the current user
  // For vendors: they can claim/renew requests from their company
  const isSameCompany = user?.companyId && submittedBy === user.companyId;
  const isSameSponsorCompany =
    user?.companyId &&
    data?.requestingSponsorEnglish &&
    user.companyId === data.requestingSponsorEnglish;
  const canActAsSponsorCompany = !isSponsorCompanyStage || Boolean(isSameSponsorCompany);
  const canRenewPendingResubmissionAfterClaim =
    checkPermission(userActionPermissions, "canRenewSecurityClearance") &&
    requestStatus === "PENDING_RESUBMISSION" &&
    (isCreator ||
      isSameCompany ||
      (isClaimed &&
        isOwner &&
        isSecurityDeptActor &&
        checkPermission(userActionPermissions, "canClaimSecurityClearance")));

  const profilePicture = documents?.find((doc) => doc.type === "PHOTOGRAPH")?.url || "";
  const spouse = applicant?.relatives?.find((item) => item.type === "SPOUSE");
  const father = applicant?.relatives?.find((item) => item.type === "FATHER");
  const mother = applicant?.relatives?.find((item) => item.type === "MOTHER");
  const bloodRelatives = applicant?.relatives?.filter((item) => item.type === "BLOOD_RELATIVE");
  const friends = applicant?.relatives?.filter((item) => item.type === "FRIEND");
  // Normalize childrenNames which may be string (legacy) or string[]
  const rawChildrenNames = applicant?.childrenNames;
  const childrenDisplay: string | undefined = (() => {
    if (Array.isArray(rawChildrenNames)) {
      return (
        rawChildrenNames
          .map((x) => (x ?? "").toString().trim())
          .filter(Boolean)
          .join(", ") || undefined
      );
    }
    if (typeof rawChildrenNames === "string") {
      const s = rawChildrenNames.trim();
      return s || undefined;
    }
    return undefined;
  })();
  const passportFront = documents?.find((item) => item.type.includes("PASSPORT_FRONT"));
  const passportBack = documents?.find((item) => item.type.includes("PASSPORT_BACK"));
  const emiratesIdFront = documents?.find((item) => item.type.includes("EMIRATES_ID_FRONT"));
  const emiratesIdBack = documents?.find((item) => item.type.includes("EMIRATES_ID_BACK"));
  const visa = documents?.filter((item) => item.type === "VISA");

  const unsuspendDialogProps = {
    testId: "security-clearance-unsuspend-dialog",
    title: t(`${LANG_BASE}.confirm-unsuspension`),
    cancelButtonText: t(`${LANG_BASE}.cancel`),
    confirmButtonText: t(`common.confirm`),
    handleCloseDialog: () => {
      if (!unsuspendRequestStartedRef.current) {
        unsuspendActionLockedRef.current = false;
        setIsUnsuspendActionLocked(false);
      }
      setUnsuspendDialogOpen(false);
    },
    confirmAction: () => {
      if (unsuspendRequestStartedRef.current || unsuspendTaskMutation.isPending) return;

      unsuspendRequestStartedRef.current = true;
      setIsUnsuspendSubmitLocked(true);
      unsuspendTaskMutation.mutate();
      setUnsuspendDialogOpen(false);
    },
    isSubmitting: unsuspendTaskMutation.isPending || isUnsuspendSubmitLocked,
    elementContent: (
      <div className="text-left">
        <p>
          <Trans
            i18nKey={`${LANG_BASE}.modal-unsuspend-description-part-one`}
            values={{
              id: applicant.passportNumber ? applicant.passportNumber : applicant.emiratesIdNumber,
            }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p>{t(`${LANG_BASE}.modal-description-part-two`)}</p>
      </div>
    ),
  };

  const cancelDialogProps = {
    testId: "security-clearance-request-cancel-dialog",
    title: t(`${LANG_BASE}.confirm-cancellation`),
    cancelButtonText: t(`${LANG_BASE}.cancel`),
    confirmButtonText: t("common.confirm"),
    handleCloseDialog: () => setCancelDialogOpen(false),
    confirmAction: () => {
      cancelMutation.mutate();
      setCancelDialogOpen(false);
    },
    elementContent: (
      <div className="text-left">
        <p>
          <Trans
            i18nKey={`${LANG_BASE}.modal-description-cancel-part-one`}
            values={{
              id: applicant.passportNumber ? applicant.passportNumber : applicant.emiratesIdNumber,
            }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p>{t(`${LANG_BASE}.modal-description-part-two`)}</p>
      </div>
    ),
  };

  const generateStaticContent = (): string => {
    if (drawerType !== "details") return "";
    const trimOrFallback = (val: string | null | undefined, fallback: string) =>
      val && val.trim().length > 0 ? val.trim() : fallback;

    if (securityClearanceDecisionHasViewableDetails(latestDecisionEvent)) {
      return (latestDecisionEvent!.reason ?? "").trim();
    }

    switch (requestStatus) {
      case "PERMANENTLY_REJECTED":
        return trimOrFallback(
          data?.permanentRejection?.reason ?? rejectionReason,
          t(`${LANG_BASE}.no-rejection-reason-provided`),
        );
      case "SUSPENDED":
        return trimOrFallback(
          data?.suspension?.reason ?? suspentionReason,
          t(`${LANG_BASE}.no-suspension-reason-provided`),
        );
      case "PENDING_RESUBMISSION":
        return trimOrFallback(latestTaskReason, t(`${LANG_BASE}.no-resubmit-reason-provided`));
      default:
        return trimOrFallback(latestTaskReason, t(`${LANG_BASE}.no-reason-provided`));
    }
  };

  return (
    <div data-testid={TEST_ID_BASE} className="bg-white" ref={componentRef}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        title={
          <div className="flex justify-between items-end mt-1">
            <div>
              <span data-testid={`${TEST_ID_BASE}-title`} className="break-all">
                {applicant?.passportNumber ? applicant.passportNumber : applicant?.emiratesIdNumber}
              </span>
              {/*@TODO -Remove hardcoded and set the info using the BE and add translation for all cases*/}
              <div className="mt-1 flex items-center">
                <Badge
                  labelTestId={`${TEST_ID_BASE}-status`}
                  type={getSecurityClearanceBadgeType(requestStatus)}
                  label={t(`${LANG_BASE}.status-labels.${requestStatus}`)}
                  bold={false}
                />
                {showViewDetailsButton && (
                  <>
                    <span className="text-base ml-2">–</span>
                    <Button
                      testId={`${TEST_ID_BASE}-view-details`}
                      variant="text"
                      type="button"
                      className="whitespace-nowrap"
                      onClick={() => setDrawerType("details")}
                      label={
                        <span className="normal-case font-medium">
                          {t(`${LANG_BASE}.view-details`)}
                        </span>
                      }
                    />
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button
                testId={`${TEST_ID_BASE}-print`}
                variant="outlined"
                type="button"
                startIcon={<PrintOutlinedIcon />}
                className="whitespace-nowrap"
                onClick={handlePrint}
                label={<span className="normal-case font-medium">{t(`${LANG_BASE}.print`)}</span>}
              />
              {requestStatus !== "PERMANENTLY_REJECTED" && (
                <>
                  {checkPermission(userActionPermissions, "canUnsuspendSecurityClearance") &&
                  requestStatus === "SUSPENDED" ? (
                    <Button
                      testId={`${TEST_ID_BASE}-unsuspend`}
                      variant="outlined"
                      type="button"
                      startIcon={<RefreshIcon />}
                      className="whitespace-nowrap"
                      disabled={
                        unsuspendTaskMutation.isPending ||
                        isUnsuspendActionLocked ||
                        isUnsuspendSubmitLocked
                      }
                      onClick={() => {
                        if (
                          unsuspendActionLockedRef.current ||
                          unsuspendTaskMutation.isPending ||
                          isUnsuspendActionLocked
                        )
                          return;
                        unsuspendActionLockedRef.current = true;
                        setIsUnsuspendActionLocked(true);
                        setUnsuspendDialogOpen(true);
                      }}
                      label={
                        <span className="normal-case font-medium">
                          {t(`${LANG_BASE}.unsuspend`)}
                        </span>
                      }
                    />
                  ) : (
                    <>
                      {checkPermission(userActionPermissions, "canRenewSecurityClearance") &&
                        (requestStatus === "APPROVED" || requestStatus === "EXPIRED") &&
                        !data?.hasPendingRenewal && (
                          <Button
                            testId={`${TEST_ID_BASE}-renew`}
                            variant="outlined"
                            type="button"
                            startIcon={<RefreshIcon />}
                            className="whitespace-nowrap"
                            onClick={handleRenew}
                            label={
                              <span className="normal-case font-medium">
                                {t(`${LANG_BASE}.renew`)}
                              </span>
                            }
                          />
                        )}

                      {checkPermission(
                        userActionPermissions,
                        "canRenewSecurityClearanceOutsideClaim",
                      ) &&
                        requestStatus === "PENDING_RESUBMISSION" &&
                        (isCreator || isSameCompany) && (
                          <Button
                            testId={`${TEST_ID_BASE}-renew`}
                            variant="outlined"
                            type="button"
                            startIcon={<RefreshIcon />}
                            className="whitespace-nowrap"
                            onClick={handleRenew}
                            label={
                              <span className="normal-case font-medium">
                                {t(`${LANG_BASE}.renew`)}
                              </span>
                            }
                          />
                        )}

                      {checkPermission(userActionPermissions, "canSuspendSecurityClearance") &&
                        requestStatus === "APPROVED" && (
                          <Button
                            testId={`${TEST_ID_BASE}-suspend`}
                            variant="outlined"
                            type="button"
                            startIcon={<DoDisturbIcon />}
                            className="whitespace-nowrap"
                            onClick={() => {
                              suspendSubmitLockedRef.current = false;
                              completionSubmitLockedRef.current = false;
                              setIsCompletionSubmitLocked(false);
                              setDrawerType("suspend");
                            }}
                            label={
                              <span className="normal-case font-medium">
                                {t(`${LANG_BASE}.suspend`)}
                              </span>
                            }
                          />
                        )}

                      {checkPermission(userActionPermissions, "canCancelSecurityClearance") &&
                        requestStatus === "APPROVED" && (
                          <Button
                            testId={`${TEST_ID_BASE}-cancel`}
                            variant="outlined"
                            type="button"
                            startIcon={<HighlightOffIcon />}
                            className="whitespace-nowrap"
                            onClick={() => setCancelDialogOpen(true)}
                            label={
                              <span className="normal-case font-medium">
                                {t(`${LANG_BASE}.cancel`)}
                              </span>
                            }
                          />
                        )}

                      {isClaimed && isOwner
                        ? requestStatus !== "APPROVED" &&
                          requestStatus !== "EXPIRED" && (
                            <>
                              {checkPermission(
                                userActionPermissions,
                                "canUnclaimSecurityClearance",
                              ) &&
                                canActAsSponsorCompany && (
                                  <Button
                                    testId={`${TEST_ID_BASE}-unclaim`}
                                    variant="outlined"
                                    type="button"
                                    className="whitespace-nowrap"
                                    onClick={() => setUnclaimDialogOpen(true)}
                                    label={
                                      <span className="normal-case font-medium">
                                        {t(`${LANG_BASE}.unclaim`)}
                                      </span>
                                    }
                                  />
                                )}
                              {checkPermission(
                                userActionPermissions,
                                "canRejectSecurityClearance",
                              ) &&
                                isSecurityApprovalStage && (
                                  <Button
                                    testId={`${TEST_ID_BASE}-permanentlyReject`}
                                    variant="outlined"
                                    type="button"
                                    startIcon={<HighlightOffIcon />}
                                    className="whitespace-nowrap"
                                    onClick={() => setDrawerType("permanentReject")}
                                    label={
                                      <span className="normal-case font-medium">
                                        {t(`${LANG_BASE}.permanently-reject`)}
                                      </span>
                                    }
                                  />
                                )}
                              {checkPermission(
                                userActionPermissions,
                                "canResubmitSecurityClearance",
                              ) &&
                                requestStatus !== "PENDING_RESUBMISSION" && (
                                  <Button
                                    testId={`${TEST_ID_BASE}-resubmit`}
                                    variant="outlined"
                                    type="button"
                                    startIcon={<SyncIcon />}
                                    className="whitespace-nowrap"
                                    onClick={() => setDrawerType("resubmit")}
                                    label={
                                      <span className="normal-case font-medium">
                                        {t(`${LANG_BASE}.resubmit`)}
                                      </span>
                                    }
                                  />
                                )}
                              {checkPermission(
                                userActionPermissions,
                                "canRenewSecurityClearance",
                              ) &&
                                canRenewPendingResubmissionAfterClaim && (
                                  <Button
                                    testId={`${TEST_ID_BASE}-renew`}
                                    variant="outlined"
                                    type="button"
                                    startIcon={<RefreshIcon />}
                                    className="whitespace-nowrap"
                                    onClick={handleRenew}
                                    label={
                                      <span className="normal-case font-medium">
                                        {t(`${LANG_BASE}.renew`)}
                                      </span>
                                    }
                                  />
                                )}
                              {checkPermission(
                                userActionPermissions,
                                "canApproveSecurityClearance",
                              ) &&
                                canActAsSponsorCompany &&
                                requestStatus !== "PENDING_RESUBMISSION" && (
                                  <Button
                                    testId={`${TEST_ID_BASE}-approve`}
                                    variant="contained"
                                    type="button"
                                    startIcon={<ContentPasteGoIcon />}
                                    className="whitespace-nowrap"
                                    onClick={handleApproveClick}
                                    label={
                                      <span className="normal-case font-medium">
                                        {t(
                                          `${LANG_BASE}.${
                                            shouldDirectVerifySecurityClearance
                                              ? "verified"
                                              : "approve"
                                          }`,
                                        )}
                                      </span>
                                    }
                                  />
                                )}
                            </>
                          )
                        : canClaimRequest &&
                          checkPermission(userActionPermissions, "canClaimSecurityClearance") &&
                          canActAsSponsorCompany &&
                          // Vendors can only claim their own company's requests
                          (requestStatus !== "PENDING_RESUBMISSION" ||
                            checkPermission(
                              userActionPermissions,
                              "canResubmitSecurityClearance",
                            ) ||
                            isCreator ||
                            isSameCompany) && (
                            <Button
                              testId={`${TEST_ID_BASE}-claim`}
                              variant="contained"
                              type="button"
                              startIcon={<ContentPasteGoIcon />}
                              className="whitespace-nowrap"
                              onClick={() => {
                                claimMutation.mutate();
                              }}
                              label={
                                <span className="normal-case font-medium">
                                  {t(`${LANG_BASE}.claim`)}
                                </span>
                              }
                            />
                          )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        }
        subtitle={
          <span>
            <span
              onClick={() => navigate("/port-security/clearances")}
              className="cursor-pointer"
              style={{ color: theme.palette.text.brand }}
            >
              {t(`${LANG_BASE}.security-clearance`)}
            </span>{" "}
            / {id}
          </span>
        }
      />

      <div className="py-8 px-24">
        {isSecuritySupervisor && (
          <div className="w-full border-t border-b border-gray-200 py-3 mb-6">
            <div className="flex justify-end gap-8">
              <div className="flex items-center gap-2">
                <span className="text-gray-700">{t(`${LANG_BASE}.verified-by`)}:</span>
                <span className="font-bold">{verificationDetails?.submittedBy || "-"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-700">{t(`${LANG_BASE}.verified-date`)}:</span>
                <span className="font-bold">
                  {verificationDetails?.timestamp
                    ? formatDateFull(verificationDetails.timestamp)
                    : "-"}
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="w-full mb-8">
          <UserProfileCard
            imageUrl={profilePicture}
            fullNameEn={applicant?.fullNameEnglish || "-"}
            fullNameAr={applicant?.fullNameArabic || "-"}
            dateOfBirth={applicant.dateOfBirth ? formatDateOnly(applicant.dateOfBirth) : "-"}
            passportNumber={applicant?.passportNumber || "-"}
            passportExpiryDate={
              applicant?.passportExpiryDate ? formatDateOnly(applicant.passportExpiryDate) : "-"
            }
            visaNo={residenceStatus?.residenceOrVisaNumber || "-"}
          />
        </div>

        <div className="mb-8 no-border">
          <UserDetailsCard
            testId={`${TEST_ID_BASE}-preview-user-details`}
            columns={2}
            leftFields={[
              {
                label: t(`${LANG_BASE}.emirates-id-number`),
                value: applicant?.emiratesIdNumber || "-",
              },
              { label: t(`${LANG_BASE}.entry-purpose-en`), value: entryPurposeEnglish || "-" },
              { label: t(`${LANG_BASE}.entry-purpose-ar`), value: entryPurposeArabic || "-" },
              {
                label: t(`${LANG_BASE}.nationality-en`),
                value: applicant?.presentNationality || "-",
              },
              {
                label: t(`${LANG_BASE}.nationality-ar`),
                value: applicant?.presentNationalityArabic || "-",
              },
              {
                label: t(`${LANG_BASE}.visitor-company-name-en`),
                value: data.visitorCompanyNameEnglish || "-",
              },
              {
                label: t(`${LANG_BASE}.visitor-company-name-ar`),
                value: data.visitorCompanyNameArabic || "-",
              },
              {
                label: t(`${LANG_BASE}.fott-entry-2`),
                value: data.fottEntry ? t("yes") : t("no"),
              },
            ]}
            rightFields={[
              { label: t(`${LANG_BASE}.uid-number`), value: uidNumber || "-" },
              { label: t(`${LANG_BASE}.occupation-en`), value: currentSponsor?.occupation || "-" },
              {
                label: t(`${LANG_BASE}.occupation-ar`),
                value: currentSponsor?.occupationArabic || "-",
              },
              {
                label: t(`${LANG_BASE}.submitted-by`),
                value: submittedByCompany
                  ? `${submittedByPreferredUsername} (${submittedBy}) / ${submittedByCompany.companyNameEnglish}`
                  : `${submittedByPreferredUsername}`,
              },
              {
                label: t(`${LANG_BASE}.security-clearance-duration`),
                value: clearanceDurationText,
              },
              {
                label: t(`${LANG_BASE}.visa-type`),
                value: getVisaTypeLabel(residenceStatus?.visaType),
              },
              {
                label: t(`${LANG_BASE}.visa-expiry-date`),
                value: residenceStatus?.expiryDate
                  ? formatDateOnly(residenceStatus.expiryDate)
                  : "-",
              },
            ]}
          />
        </div>

        <div className="mb-8 no-border">
          <div className="mb-3 font-semibold no-break text-gray-700 text-lg">
            {attachmentsLabel}
          </div>
          <div
            className="border rounded-md px-4 pb-1 pt-4 mb-6 mt-6 no-border no-break"
            style={getBorderBoxStyle(theme)}
          >
            <div className="flex flex-col md:flex-row gap-6 mb-4">
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${passportFront?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (passportFront?.url) handlePreview(passportFront.url);
                      }}
                    >
                      {t(`${LANG_BASE}.view-passport-front`)}
                    </a>
                  </span>
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${passportBack?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (passportBack?.url) handlePreview(passportBack.url);
                      }}
                    >
                      {t(`${LANG_BASE}.view-passport-back`)}
                    </a>
                  </span>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${emiratesIdFront?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (emiratesIdFront?.url) handlePreview(emiratesIdFront.url);
                      }}
                    >
                      {t(`${LANG_BASE}.view-emirates-id-front`)}
                    </a>
                  </span>
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${emiratesIdBack?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (emiratesIdBack?.url) handlePreview(emiratesIdBack.url);
                      }}
                    >
                      {t(`${LANG_BASE}.view-emirates-id-back`)}
                    </a>
                  </span>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${visa?.length && visa[0]?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (visa?.length && visa[0]?.url) handlePreview(visa[0].url);
                      }}
                    >
                      {t(`${LANG_BASE}.view-visa`)}
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 no-border">
          <Accordion
            testId={`${TEST_ID_BASE}-self-introduction-1`}
            summary={t(`${LANG_BASE}.preview-steps.self-introduction-1/3`)}
            expanded={expandedAccordion === `${TEST_ID_BASE}-self-introduction-1`}
            onChange={(_, isExpanded) =>
              setExpandedAccordion(isExpanded ? `${TEST_ID_BASE}-self-introduction-1` : false)
            }
          >
            <div
              className="border rounded-md px-4 pb-1 pt-4 mb-6 mt-6 no-border no-break"
              style={getBorderBoxStyle(theme)}
            >
              <div className="mb-3 font-semibold no-break">{t(`${LANG_BASE}.bio-data`)}</div>
              <UserDetailsCard
                testId={`${TEST_ID_BASE}-bio-data`}
                columns={2}
                lastItemBorder={false}
                leftFields={[
                  { label: t(`${LANG_BASE}.surname`), value: applicant?.surname || "-" },
                  { label: t(`${LANG_BASE}.alias`), value: applicant?.alias || "-" },
                  {
                    label: t(`${LANG_BASE}.place-of-birth`),
                    value: getCountryLabel(applicant?.placeOfBirth),
                  },
                  {
                    label: t(`${LANG_BASE}.date-of-birth`),
                    value: applicant?.dateOfBirth ? formatDateOnly(applicant.dateOfBirth) : "-",
                  },
                  {
                    label: t(`${LANG_BASE}.past-nationality`),
                    value: applicant?.pastNationality || "-",
                  },
                ]}
                rightFields={[
                  { label: t(`${LANG_BASE}.sect`), value: applicant?.sect || "-" },
                  { label: t(`${LANG_BASE}.religion`), value: applicant?.religion || "-" },
                  {
                    label: t(`${LANG_BASE}.individual-email-address`),
                    value: applicant?.emailAddress || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.individual-mobile-number`),
                    value: applicant?.mobileNumber || "-",
                  },
                  { label: t(`${LANG_BASE}.place-of-entry`), value: data.placeOfEntry || "-" },
                  {
                    label: t(`${LANG_BASE}.date-of-entry`),
                    value: data.dateOfEntry ? formatDateOnly(data.dateOfEntry) : "-",
                  },
                  { label: t(`${LANG_BASE}.sponsor-upon-entry`), value: sponsorOfEntry || "-" },
                ]}
              />
            </div>

            <div className="mb-8 no-border">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 mt-6 no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.current-sponsor-data`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-current-sponsor-data`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.current-sponsor-2`),
                      value: currentSponsor?.name || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.occupation-en`),
                      value: currentSponsor?.occupation || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.occupation-ar`),
                      value: currentSponsor?.occupationArabic || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.present-workplace`),
                      value: currentSponsor?.presentWorkplace || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.office-fax`),
                      value: currentSponsor?.officeFax || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.office-telephone`),
                      value: currentSponsor?.officeTelephone || "-",
                    },
                    { label: t(`${LANG_BASE}.bank`), value: currentSponsor?.bank || "-" },
                    { label: t(`${LANG_BASE}.salary`), value: currentSponsor?.salary || "-" },
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-6 items-start">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.particulars-of-passport`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-of-passport`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.place-of-issue`),
                      value: stringToTitleCase(
                        countries.find(
                          (c) =>
                            c.countryCode?.toUpperCase() ===
                            applicant?.passportPlaceOfIssue?.toUpperCase(),
                        )?.countryName ?? "-",
                      ),
                    },
                    {
                      label: t(`${LANG_BASE}.passport-number`),
                      value: (applicant?.passportNumber || "-").toUpperCase(),
                    },
                    {
                      label: t(`${LANG_BASE}.expiry-date`),
                      value: applicant?.passportExpiryDate
                        ? formatDateOnly(applicant.passportExpiryDate)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.date-of-issue`),
                      value: applicant?.passportDateOfIssue
                        ? formatDateOnly(applicant.passportDateOfIssue)
                        : "-",
                    },
                  ]}
                />
              </div>

              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold">
                  {t(`${LANG_BASE}.passport-front-and-back`)}
                </div>
                <div className="flex flex-col gap-6 mb-4">
                  {passportFront || passportBack ? (
                    <>
                      <div className="w-full" key={passportFront?.url}>
                        <FilePreview
                          testId={`${TEST_ID_BASE}-passport-front`}
                          title={passportFront?.originalFileName || ""}
                          imageUrl={passportFront?.url || ""}
                          onPreview={(url) => downloadedFilePreview(url)}
                          enablePreviewZoom
                        />
                      </div>
                      <div className="w-full" key={passportBack?.url}>
                        <FilePreview
                          testId={`${TEST_ID_BASE}-passport-back`}
                          title={passportBack?.originalFileName || ""}
                          imageUrl={passportBack?.url || ""}
                          onPreview={(url) => downloadedFilePreview(url)}
                          enablePreviewZoom
                        />
                      </div>
                    </>
                  ) : (
                    <div className="w-full">{t("common.notApplicable")}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-6 items-start">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.particular-of-emirates-id`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-of-emirates-id`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.place-of-issue`),
                      value: applicant?.emiratesIdPlaceOfIssue || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.emirates-id-number`),
                      value: applicant?.emiratesIdNumber || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.expiry-date`),
                      value: applicant?.emiratesIdExpiryDate
                        ? formatDateOnly(applicant.emiratesIdExpiryDate)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.date-of-issue`),
                      value: applicant?.emiratesIdDateOfIssue
                        ? formatDateOnly(applicant.emiratesIdDateOfIssue)
                        : "-",
                    },
                  ]}
                />
              </div>

              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold">
                  {t(`${LANG_BASE}.emiratesId-front-and-back`)}
                </div>
                <div className="flex flex-col gap-6 mb-4">
                  {emiratesIdFront || emiratesIdBack ? (
                    <>
                      <div className="w-full" key={emiratesIdFront?.url}>
                        <FilePreview
                          testId={`${TEST_ID_BASE}-emiratesId-front`}
                          title={emiratesIdFront?.originalFileName || ""}
                          imageUrl={emiratesIdFront?.url || ""}
                          onPreview={(url) => downloadedFilePreview(url)}
                          enablePreviewZoom
                        />
                      </div>
                      <div className="w-full" key={emiratesIdBack?.url}>
                        <FilePreview
                          testId={`${TEST_ID_BASE}-emiratesId-back`}
                          title={emiratesIdBack?.originalFileName || ""}
                          imageUrl={emiratesIdBack?.url || ""}
                          onPreview={(url) => downloadedFilePreview(url)}
                          enablePreviewZoom
                        />
                      </div>
                    </>
                  ) : (
                    <div className="w-full">{t("common.notApplicable")}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:gap-6 items-start">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.particulars-of-residence-visa`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-of-residence-visa`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.place-of-issue`),
                      value: residenceStatus?.placeOfIssueCode || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.residence-visa-no`),
                      value: residenceStatus?.residenceOrVisaNumber || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.visa-type`),
                      value: getVisaTypeLabel(residenceStatus?.visaType),
                    },
                    {
                      label: t(`${LANG_BASE}.date-of-issue`),
                      value: residenceStatus?.dateOfIssue
                        ? formatDateOnly(residenceStatus.dateOfIssue)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.expiry-date`),
                      value: residenceStatus?.expiryDate
                        ? formatDateOnly(residenceStatus.expiryDate)
                        : "-",
                    },
                  ]}
                />
              </div>
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold">{t(`${LANG_BASE}.visa`)}</div>
                <div className="flex flex-col gap-6 mb-4">
                  {visa?.length ? (
                    visa.map((item, idx) => (
                      <div className="w-full" key={item.url || idx}>
                        <FilePreview
                          testId={`${TEST_ID_BASE}-visa-${idx}`}
                          title={item.originalFileName || ""}
                          imageUrl={item.url || ""}
                          onPreview={(url) => downloadedFilePreview(url)}
                          enablePreviewZoom
                        />
                      </div>
                    ))
                  ) : (
                    <div className="w-full md:w-[48%]">{t("common.notApplicable")}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between items-start">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.academic-qualifications`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-academic-qualifications`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.academic-qualification`),
                      value: academicQualifications?.qualification || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.university`),
                      value: academicQualifications?.school || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.place-of-study`),
                      value: academicQualifications?.placeOfStudy || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.certificate-date`),
                      value: academicQualifications?.certificateDate
                        ? formatDateOnly(academicQualifications?.certificateDate)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.languages-known`),
                      value: academicQualifications?.languagesKnown || "-",
                    },
                  ]}
                />
              </div>
            </div>
          </Accordion>
        </div>

        <div className="mb-8 no-border">
          <Accordion
            testId={`${TEST_ID_BASE}-self-introduction-2`}
            summary={t(`${LANG_BASE}.preview-steps.self-introduction-2/3`)}
          >
            <div className="flex flex-col md:flex-row md:justify-between items-start mt-6">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">{t(`${LANG_BASE}.social-status`)}</div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-social-status`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.spouse-name-2`),
                      value: spouse?.name || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.nationality`),
                      value: spouse?.nationality || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.place-of-birth`),
                      value: getCountryLabel(spouse?.placeOfBirth),
                    },
                    {
                      label: t(`${LANG_BASE}.date-of-birth`),
                      value: spouse?.dateOfBirth ? formatDateOnly(spouse.dateOfBirth) : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.occupation`),
                      value: spouse?.occupation || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.work-place`),
                      value: spouse?.placeOfWork || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.children`),
                      value: childrenDisplay || "-",
                    },
                  ]}
                />
              </div>

              <div className="w-full md:w-[calc(50%-12px)]">
                {!!bloodRelatives?.length &&
                  bloodRelatives.map((person, index) => (
                    <div
                      className="border rounded-md px-4 pb-1 pt-4 mb-6  no-border no-break"
                      style={getBorderBoxStyle(theme)}
                      key={person?.name}
                    >
                      <div className="mb-3 font-semibold no-break">
                        {t(`${LANG_BASE}.particulars-of-blood-relations`)}
                      </div>
                      <UserDetailsCard
                        testId={`${TEST_ID_BASE}-particulars-of-blood-relations-${index + 1}`}
                        columns={1}
                        lastItemBorder={false}
                        leftFields={[
                          {
                            label: t(`${LANG_BASE}.name`),
                            value: person?.name || "-",
                          },
                          {
                            label: t(`${LANG_BASE}.relation`),
                            value: person?.relationship || "-",
                          },
                          {
                            label: t(`${LANG_BASE}.nationality`),
                            value: person?.nationality || "-",
                          },
                          {
                            label: t(`${LANG_BASE}.work-place`),
                            value: person?.placeOfWork || "-",
                          },
                        ]}
                      />
                    </div>
                  ))}
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between items-start">
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.particulars-of-blood-relations-from-first-grade`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-of-blood-relations-from-first-grade`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.father-name`),
                      value: father?.name || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.nationality`),
                      value: father?.nationality || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.birth-place`),
                      value: getCountryLabel(father?.placeOfBirth),
                    },
                    {
                      label: t(`${LANG_BASE}.birth-date`),
                      value: father?.dateOfBirth ? formatDateOnly(father.dateOfBirth) : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.work-place`),
                      value: father?.placeOfWork || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.occupation`),
                      value: father?.occupation || "-",
                    },

                    {
                      label: t(`${LANG_BASE}.mother-name`),
                      value: mother?.name || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.nationality`),
                      value: mother?.nationality || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.birth-place`),
                      value: getCountryLabel(mother?.placeOfBirth),
                    },
                    {
                      label: t(`${LANG_BASE}.birth-date`),
                      value: mother?.dateOfBirth ? formatDateOnly(mother?.dateOfBirth) : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.work-place`),
                      value: mother?.placeOfWork || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.occupation`),
                      value: mother?.occupation || "-",
                    },
                  ]}
                />
              </div>
              <div
                className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.visitor-particulars-of-residence`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-residence`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    { label: t(`${LANG_BASE}.zone`), value: residency?.zone || "-" },
                    { label: t(`${LANG_BASE}.owner-name-2`), value: residency?.ownerName || "-" },
                    { label: t(`${LANG_BASE}.street`), value: residency?.street || "-" },
                    { label: t(`${LANG_BASE}.phone-no`), value: residency?.phoneNumber || "-" },
                    { label: t(`${LANG_BASE}.mobile-no`), value: residency?.mobileNumber || "-" },
                    {
                      label: t(`${LANG_BASE}.building-no`),
                      value: residency?.buildingNumber || "-",
                    },
                    { label: t(`${LANG_BASE}.pager-no`), value: residency?.pagerNumber || "-" },
                    { label: t(`${LANG_BASE}.flat-no`), value: residency?.flatNumber || "-" },
                    { label: t(`${LANG_BASE}.po-box-no`), value: residency?.poBoxNumber || "-" },
                    { label: t(`${LANG_BASE}.floor-no`), value: residency?.floorNumber || "-" },
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between items-start">
              <div className="w-full md:w-[calc(50%-12px)]">
                {!!friends?.length &&
                  friends.map((friend, index) => (
                    <div
                      className="border rounded-md px-4 pb-1 pt-4 mb-6  no-border no-break"
                      style={getBorderBoxStyle(theme)}
                      key={friend?.name}
                    >
                      <div className="mb-3 font-semibold no-break">
                        {t(`${LANG_BASE}.particular-of-friends`)}
                      </div>
                      <UserDetailsCard
                        testId={`${TEST_ID_BASE}-particular-of-friends-${index + 1}`}
                        columns={1}
                        lastItemBorder={false}
                        leftFields={[
                          {
                            label: t(`${LANG_BASE}.name`),
                            value: friend?.name || "-",
                          },
                          {
                            label: t(`${LANG_BASE}.nationality`),
                            value: friend?.nationality || "-",
                          },
                          {
                            label: t(`${LANG_BASE}.work-place`),
                            value: friend?.placeOfWork || "-",
                          },
                        ]}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </Accordion>
        </div>

        <div className="mb-8 no-border">
          <Accordion
            testId={`${TEST_ID_BASE}-self-introduction-3`}
            summary={t(`${LANG_BASE}.preview-steps.self-introduction-3/3`)}
          >
            <div className="flex flex-col md:flex-row md:justify-between items-start mt-6">
              <div
                className="border rounded-md px-4 pb-1 pt-4 w-full md:w-[calc(50%-12px)] no-border no-break"
                style={getBorderBoxStyle(theme)}
              >
                <div className="mb-3 font-semibold no-break">
                  {t(`${LANG_BASE}.particulars-of-vehicle`)}
                </div>
                <UserDetailsCard
                  testId={`${TEST_ID_BASE}-particulars-of-vehicle`}
                  columns={1}
                  lastItemBorder={false}
                  leftFields={[
                    {
                      label: t(`${LANG_BASE}.type-of-vehicle`),
                      value: vehicleDetails?.type || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.driving-license-number`),
                      value: vehicleDetails?.drivingLicenseNumber || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.place-of-issue`),
                      value: vehicleDetails?.placeOfIssue || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.licensed-by`),
                      value: vehicleDetails?.licensedBy || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.date-of-issue`),
                      value: vehicleDetails?.issueDate
                        ? formatDateOnly(vehicleDetails?.issueDate)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.plate-number`),
                      value: vehicleDetails?.plateNo || "-",
                    },
                    {
                      label: t(`${LANG_BASE}.license-expiry-date`),
                      value: vehicleDetails?.expiryDate
                        ? formatDateOnly(vehicleDetails?.expiryDate)
                        : "-",
                    },
                    {
                      label: t(`${LANG_BASE}.plate-code`),
                      value: vehicleDetails?.plateCode || "-",
                    },
                  ]}
                />
              </div>

              <div className="w-full md:w-[calc(50%-12px)]">
                <div
                  className="border rounded-md px-4 pb-1 pt-4 mb-6 no-border no-break"
                  style={getBorderBoxStyle(theme)}
                >
                  <div className="mb-3 font-semibold no-break">
                    {t(`${LANG_BASE}.more-details`)}
                  </div>
                  <UserDetailsCard
                    testId={`${TEST_ID_BASE}-more-details`}
                    columns={1}
                    lastItemBorder={false}
                    leftFields={[
                      {
                        label: t(`${LANG_BASE}.previous-occupations`),
                        value: applicant?.previousOccupations?.join(", ") || "-",
                      },
                      {
                        label: t(`${LANG_BASE}.countries-visited`),
                        value:
                          applicant?.visitedCountries
                            ?.map((vc) =>
                              stringToTitleCase(
                                countries.find(
                                  (c) => c.countryCode?.toUpperCase() === vc.toUpperCase(),
                                )?.countryName ?? "-",
                              ),
                            )
                            .join(", ") || "-",
                      },
                      {
                        label: t(`${LANG_BASE}.countries-worked-in`),
                        value:
                          applicant?.countriesWorkedIn
                            ?.map((vc) =>
                              stringToTitleCase(
                                countries.find(
                                  (c) => c.countryCode?.toUpperCase() === vc.toUpperCase(),
                                )?.countryName ?? "-",
                              ),
                            )
                            .join(", ") || "-",
                      },
                      {
                        label: t(`${LANG_BASE}.military-service-question`),
                        value: militaryService?.hasServed
                          ? t(`${LANG_BASE}.yes`)
                          : t(`${LANG_BASE}.no`),
                      },
                      ...(militaryService?.hasServed
                        ? [
                            {
                              label: t(`${LANG_BASE}.country-name`),
                              value: stringToTitleCase(
                                countries.find(
                                  (c) =>
                                    c.countryCode?.toUpperCase() ===
                                    militaryService?.country?.toUpperCase(),
                                )?.countryName ?? "-",
                              ),
                            },
                            {
                              label: t(`${LANG_BASE}.service-type`),
                              value: militaryService?.typeOfService || "-",
                            },
                            {
                              label: t(`${LANG_BASE}.rank`),
                              value: militaryService?.rank || "-",
                            },
                            {
                              label: t(`${LANG_BASE}.service-duration`),
                              value: militaryService?.duration || "-",
                            },
                            {
                              label: t(`${LANG_BASE}.military-service-start-date`),
                              value: militaryService?.startDate
                                ? formatDateOnly(militaryService?.startDate)
                                : "-",
                            },
                            {
                              label: t(`${LANG_BASE}.military-service-end-date`),
                              value: militaryService?.endDate
                                ? formatDateOnly(militaryService?.endDate)
                                : "-",
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
                <div
                  className="border rounded-md px-4 pb-1 pt-4 mb-6 w-full no-border no-break"
                  style={getBorderBoxStyle(theme)}
                >
                  <div className="mb-3 font-semibold no-break">
                    {t(
                      `${LANG_BASE}.i-undertake-that-the-details-given-above-are-correct-and-complete`,
                    )}
                  </div>
                  <UserDetailsCard
                    testId={`${TEST_ID_BASE}-i-undertake-that-the-details-given-above-are-correct-and-complete`}
                    columns={1}
                    lastItemBorder={false}
                    leftFields={[
                      {
                        label: t(`${LANG_BASE}.signature-name`),
                        value: signature?.signerName || "-",
                      },
                      {
                        label: t(`${LANG_BASE}.signature-date`),
                        value: signature?.signatureDate
                          ? formatDateOnly(signature?.signatureDate)
                          : "-",
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </Accordion>
        </div>

        <div className="mb-8 no-border">
          <Accordion
            testId={`${TEST_ID_BASE}-visit-details-and-documents`}
            summary={t(`${LANG_BASE}.preview-steps.visit-details-and-documents`)}
          >
            <div
              className="border rounded-md px-4 pb-1 pt-4 mb-6 mt-6 no-border no-break"
              style={getBorderBoxStyle(theme)}
            >
              <div className="mb-3 font-semibold no-break">{t(`${LANG_BASE}.visit-details`)}</div>
              <UserDetailsCard
                testId={`${TEST_ID_BASE}-visit-details`}
                columns={2}
                lastItemBorder={false}
                leftFields={[
                  {
                    label: t(`${LANG_BASE}.visitor-company-name`),
                    value: data.visitorCompanyNameEnglish || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.entry-purpose-en`),
                    value: data.entryPurposeEnglish || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.entry-purpose-ar`),
                    value: data.entryPurposeArabic || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.fott-entry-2`),
                    value: data.fottEntry ? t("yes") : t("no"),
                  },
                  {
                    label: t(`${LANG_BASE}.requesting-sponsor-email-address`),
                    value: data?.requestingSponsorEmailAddress || "-",
                  },
                ]}
                rightFields={[
                  { label: t(`${LANG_BASE}.uid-number`), value: data.uidNumber || "-" },
                  {
                    label: t(`${LANG_BASE}.requesting-sponsor-company-en`),
                    value: requestingSponsorCompany?.companyNameEnglish || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.requesting-sponsor-company-ar`),
                    value: requestingSponsorCompany?.companyNameArabic || "-",
                  },
                  {
                    label: t(`${LANG_BASE}.security-clearance-validity`),
                    value: validityPeriodIntToString(Number(validityPeriod), t),
                  },
                ]}
              />
            </div>

            <div
              className="border rounded-md px-4 pb-1 pt-4 mb-6 no-border no-break"
              style={getBorderBoxStyle(theme)}
            >
              <div className="mb-3 font-semibold">{t(`${LANG_BASE}.passport-front-and-back`)}</div>
              <div className="flex flex-row xl:flex-row gap-6 mb-4">
                {passportFront || passportBack ? (
                  <>
                    <div className="w-full md:w-[48%]" key={passportFront?.url}>
                      <FilePreview
                        testId={`${TEST_ID_BASE}-passport-front`}
                        title={passportFront?.originalFileName || ""}
                        imageUrl={passportFront?.url || ""}
                        onPreview={(url) => downloadedFilePreview(url)}
                      />
                    </div>
                    <div className="w-full md:w-[48%]" key={passportBack?.url}>
                      <FilePreview
                        testId={`${TEST_ID_BASE}-passport-back`}
                        title={passportBack?.originalFileName || ""}
                        imageUrl={passportBack?.url || ""}
                        onPreview={(url) => downloadedFilePreview(url)}
                      />
                    </div>
                  </>
                ) : (
                  <div className="w-full md:w-[48%]">{t("common.notApplicable")}</div>
                )}
              </div>
            </div>

            <div
              className="border rounded-md px-4 pb-1 pt-4 mb-6 no-border no-break"
              style={getBorderBoxStyle(theme)}
            >
              <div className="mb-3 font-semibold">
                {t(`${LANG_BASE}.emiratesId-front-and-back`)}
              </div>
              <div className="flex flex-row xl:flex-row gap-6 mb-4">
                {emiratesIdFront || emiratesIdBack ? (
                  <>
                    <div className="w-full md:w-[48%]" key={emiratesIdFront?.url}>
                      <FilePreview
                        testId={`${TEST_ID_BASE}-emiratesId-front`}
                        title={emiratesIdFront?.originalFileName || ""}
                        imageUrl={emiratesIdFront?.url || ""}
                        onPreview={(url) => downloadedFilePreview(url)}
                      />
                    </div>
                    <div className="w-full md:w-[48%]" key={emiratesIdBack?.url}>
                      <FilePreview
                        testId={`${TEST_ID_BASE}-emiratesId-back`}
                        title={emiratesIdBack?.originalFileName || ""}
                        imageUrl={emiratesIdBack?.url || ""}
                        onPreview={(url) => downloadedFilePreview(url)}
                      />
                    </div>
                  </>
                ) : (
                  <div className="w-full md:w-[48%]">{t("common.notApplicable")}</div>
                )}
              </div>
            </div>

            <div
              className="border rounded-md px-4 pb-1 pt-4 mb-6 no-border no-break"
              style={getBorderBoxStyle(theme)}
            >
              <div className="mb-3 font-semibold">{t(`${LANG_BASE}.visa`)}</div>
              <div className="flex flex-row md:flex-row gap-6 mb-4">
                {visa?.length ? (
                  visa.map((item, idx) => (
                    <div className="w-full md:w-[48%]" key={item.url || idx}>
                      <FilePreview
                        testId={`${TEST_ID_BASE}-visa-${idx}`}
                        title={item.originalFileName || ""}
                        imageUrl={item.url || ""}
                        onPreview={(url) => downloadedFilePreview(url)}
                      />
                    </div>
                  ))
                ) : (
                  <div className="w-full md:w-[48%]">{t("common.notApplicable")}</div>
                )}
              </div>
            </div>
          </Accordion>
        </div>
      </div>
      <GenericDialog open={cancelDialogOpen} {...cancelDialogProps} />
      <ConfirmUnclaimDialog
        isOpen={unclaimDialogOpen}
        setIsOpen={setUnclaimDialogOpen}
        confirmAction={() => {
          unclaimMutation.mutate();
        }}
        itemId={applicant.passportNumber ? applicant.passportNumber : applicant.emiratesIdNumber}
        languageBase={LANG_BASE}
        testId="security-clearance-request-preview-dialog"
      />
      <GenericDialog open={unsuspendDialogOpen} {...unsuspendDialogProps} />

      <SideDrawer
        type={drawerType!}
        isOpen={drawerType !== null}
        onClose={() => setDrawerType(null)}
        title={
          drawerType == null
            ? ""
            : drawerType === "approval"
              ? t(`${LANG_BASE}.approve-security-clearance`)
              : drawerType === "resubmit"
                ? t(`${LANG_BASE}.resubmit-security-clearance`)
                : drawerType === "details"
                  ? t(`${LANG_BASE}.details`)
                  : drawerType === "permanentReject"
                    ? t(`${LANG_BASE}.permanently-reject-security-clearance`)
                    : t(`${LANG_BASE}.suspend-security-clearance`)
        }
        description={
          drawerType == null
            ? ""
            : drawerType === "approval"
              ? t(`${LANG_BASE}.you-are-about-to-approve-security-clearance`)
              : drawerType === "permanentReject"
                ? t(`${LANG_BASE}.permanently-reject-confirmation`)
                : drawerType === "resubmit"
                  ? t(`${LANG_BASE}.resubmit-confirmation`)
                  : ""
        }
        notesLabel={
          drawerType == null
            ? ""
            : drawerType === "approval"
              ? t(`${LANG_BASE}.notes`)
              : drawerType === "resubmit"
                ? t(`${LANG_BASE}.resubmission-reason`)
                : drawerType === "details"
                  ? requestStatus === "PERMANENTLY_REJECTED"
                    ? t(`${LANG_BASE}.rejection-reason`)
                    : requestStatus === "PENDING_RESUBMISSION"
                      ? t(`${LANG_BASE}.pending-resubmission-reason`)
                      : requestStatus === "SUSPENDED"
                        ? t(`${LANG_BASE}.suspension-reason`)
                        : `${
                            latestTask?.taskDecision?.status
                              ? `Status: ${t(
                                  `security-clearance.status-labels.${latestTask.taskDecision.status}`,
                                )}`
                              : "-"
                          }\nSubmitted by: ${latestTask?.taskDecision?.submittedBy ?? "-"}`
                  : drawerType === "permanentReject"
                    ? t(`${LANG_BASE}.rejection-reason`)
                    : t(`${LANG_BASE}.suspension-reason`)
        }
        submitButtonLabel={
          drawerType == null
            ? ""
            : drawerType === "approval"
              ? t(`${LANG_BASE}.approve`)
              : drawerType === "resubmit"
                ? t(`${LANG_BASE}.resubmit-drawer-button`)
                : drawerType === "details"
                  ? t(`common.ok`)
                  : drawerType === "permanentReject"
                    ? t(`${LANG_BASE}.permanently-reject`)
                    : t(`${LANG_BASE}.suspend`)
        }
        staticContent={generateStaticContent()}
        onSubmit={handleSubmit}
        onDocumentUpload={(file) => {
          if (file) {
            setUploadedDecisionAttachments([]);
            uploadDocuments.mutate({
              file,
            });
          }
        }}
        requestId={applicant.passportNumber ? applicant.passportNumber : applicant.emiratesIdNumber}
        fileUploadTitle={attachmentsLabel}
        additionalFileUploadTitle={attachmentsLabel}
        showChangeValidityPeriod={canChangeValidityPeriod && shouldShowValidityPeriod}
        dropdownLabel={t(`${LANG_BASE}.validity-period`)}
        dropdownOptions={validityPeriodsOptions}
        dropdownCurrentOption={validityPeriodIntToString(Number(validityPeriod), t)}
        detailsAttachments={drawerType === "details" ? timelineDetailsAttachments : undefined}
        //Hidden History until we clarify requirements
        //detailsHistory={drawerType === "details" ? sortedTasksHistory.slice(1) : undefined}
        onAttachmentDownload={handleAttachmentDownload}
        onPreview={(url?: string) => {
          if (url) return handlePreview(url);
        }}
        onRemoveUploadedAttachmentUrl={() => {
          setUploadedDecisionAttachments([]);
        }}
        isExternalDocumentUploadPending={isDecisionAttachmentUploading}
        shouldShowLoadingFileToast
        isSubmitting={isCompletionSubmitLocked || suspendTaskMutation.isPending}
      />
    </div>
  );
};

export default SecurityClearanceRequestPreview;
